import React, { useEffect, useState } from "react";
import { Container, Col, Row, Form, FloatingLabel } from "react-bootstrap";
import { NavBar } from "../component/NavBar";
import { Footer } from "../component/Footer";
import { Loader } from "../component/loader";
import { FaLinkedin, FaMapMarkerAlt, FaEnvelope, FaPhoneAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import ReactGA from "react-ga4";
import "./Contact.css"; // Make sure to import the CSS

// --- Map Component (Internal) ---
const ResponsiveMap = () => {
  return (
    <div className="map-container">
      <iframe
        title="Techsa Location"
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3770.3795074536033!2d72.91394231456822!3d19.0910000564626!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c7cc849925a9%3A0xae6b724d51049e67!2sTechsa+Services+Private+Limited!5e0!3m2!1sen!2s!4v1471602687866"
        className="map-frame"
        allowFullScreen=""
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"></iframe>
    </div>
  );
};

export const Contact = () => {
  const [loading, setLoading] = useState(true);
  const [formDetails, setFormDetails] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });
  const [buttonText, setButtonText] = useState("Send Message");
  
  ReactGA.initialize("G-6WD5GM86HD");
  
  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname + window.location.search,
    });
    setTimeout(() => setLoading(false), 1000);
  }, []);

  const onFormUpdate = (category, value) => {
    setFormDetails({ ...formDetails, [category]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formDetails.firstName || !formDetails.email || !formDetails.message) {
      toast.error("Please fill in the required fields.");
      return;
    }

    setButtonText("Sending...");

    try {
      await pushMessage(
        `${formDetails.firstName} ${formDetails.lastName}`,
        formDetails.email,
        formDetails.message,
        formDetails.phone
      );
      setButtonText("Message Sent!");
      setFormDetails({ firstName: "", lastName: "", email: "", phone: "", message: "" });
      toast.success("Thank you! We will contact you shortly.");
      
      // Reset button text after 3 seconds
      setTimeout(() => setButtonText("Send Message"), 3000);
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong. Please try again.");
      setButtonText("Send Message");
    }
  };

  const pushMessage = async (name, email, message, phone = "000") => {
    const messageBody = {
      title: `Contact Form (Website) - ${name}`,
      body: `Name: ${name}\nMessage: ${message}\nEmail: ${email}\nPhone: ${phone}`,
      type: "note",
    };

    const pushbulletResponse = await fetch("/push.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(messageBody),
    });

    if (!pushbulletResponse.ok) throw new Error("API request failed");
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div>
          <Helmet>
            <title>Contact Us - Techsa Services Pvt. Ltd.</title>
            <meta name="description" content="Contact Techsa Services Pvt. Ltd. today." />
          </Helmet>
          <NavBar />
          
          <section className="contact-section" id="connect">
            <Container>
              <Row className="justify-content-center">
                {/* --- Left Column: The Form --- */}
                <Col lg={6} className="mb-5 mb-lg-0">
                  <div className="contact-form-wrapper">
                    <div className="form-header">
                      <h2>Get in Touch</h2>
                      <p>Fill out the form below and our team will get back to you.</p>
                    </div>

                    <Form onSubmit={handleSubmit}>
                      <Row>
                        <Col md={6}>
                          <FloatingLabel controlId="firstName" label="First Name" className="mb-3">
                            <Form.Control
                              type="text"
                              placeholder="First Name"
                              value={formDetails.firstName}
                              onChange={(e) => onFormUpdate("firstName", e.target.value)}
                            />
                          </FloatingLabel>
                        </Col>
                        <Col md={6}>
                          <FloatingLabel controlId="lastName" label="Last Name" className="mb-3">
                            <Form.Control
                              type="text"
                              placeholder="Last Name"
                              value={formDetails.lastName}
                              onChange={(e) => onFormUpdate("lastName", e.target.value)}
                            />
                          </FloatingLabel>
                        </Col>
                      </Row>

                      <FloatingLabel controlId="email" label="Email Address" className="mb-3">
                        <Form.Control
                          type="email"
                          placeholder="name@example.com"
                          value={formDetails.email}
                          onChange={(e) => onFormUpdate("email", e.target.value)}
                        />
                      </FloatingLabel>

                      <FloatingLabel controlId="phone" label="Phone Number" className="mb-3">
                        <Form.Control
                          type="tel"
                          placeholder="1234567890"
                          maxLength={10}
                          value={formDetails.phone}
                          onChange={(e) => onFormUpdate("phone", e.target.value)}
                        />
                      </FloatingLabel>

                      <FloatingLabel controlId="message" label="Your Message" className="mb-4">
                        <Form.Control
                          as="textarea"
                          placeholder="Leave a comment here"
                          style={{ height: '150px' }}
                          value={formDetails.message}
                          onChange={(e) => onFormUpdate("message", e.target.value)}
                        />
                      </FloatingLabel>

                      <div className="d-flex align-items-center justify-content-between">
                        <button type="submit" className="custom-button">
                          <span>{buttonText}</span>
                        </button>
                        
                        <Link
                          to="https://in.linkedin.com/company/techsa-services-pvt-ltd"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ms-3 text-primary"
                        >
                          <FaLinkedin size={36} color="#0077b5" />
                        </Link>
                      </div>
                    </Form>
                  </div>
                </Col>

                {/* --- Right Column: Info & Map --- */}
                <Col lg={6}>
                  <div className="info-map-wrapper">
                    <div className="contact-info-box">
                      <h4 className="mb-4 text-white">Contact Information</h4>
                      
                      <div className="info-item">
                        <FaMapMarkerAlt className="info-icon" />
                        <div>
                          <strong>Techsa Services Private Limited</strong><br />
                          B Wing, Damji Shamji Corporate Square,<br />
                          Ghatkopar East, Mumbai, 400075
                        </div>
                      </div>

                      {/* <div className="info-item">
                        <FaEnvelope className="info-icon" />
                        <div>
                          <strong>Email Us</strong><br />
                          sales@techsa.net
                        </div>
                      </div>

                      <div className="info-item mb-0">
                        <FaPhoneAlt className="info-icon" />
                        <div>
                          <strong>Call Us</strong><br />
                          +91 22 1234 5678
                        </div>
                      </div> */}
                    </div>
                    
                    {/* The Map Component */}
                    <ResponsiveMap />
                  </div>
                </Col>
              </Row>
            </Container>
          </section>
          
          <Footer />
        </div>
      )}
    </>
  );
};